import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  TextField,
  Grid,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Typography
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { useGroups } from "../../hooks/apiHooks/groups";
import { useLocations } from "../../hooks/apiHooks/locations";
import { usePositions } from "../../hooks/apiHooks/positions";
import useScopedTranslation from "../../hooks/useScopedTranslation";
import SearchInput from "../../components/SearchInput/SearchInput";

const useStyles = makeStyles(() => ({
  checkbox: {
    margin: 0
  }
}));

const ASSIGNMENT_TYPES = ["location", "position", "group"];

const arraysEqual = (array1, array2) => {
  return (
    array1.length === array2.length &&
    array1.every(item => array2.includes(item))
  );
};

const AssignmentSelector = ({ onAssignmentDataChange, assignmentData }) => {
  const t = useScopedTranslation("bulkAssignmentTool.assignmentSelector");

  const { getLocations, isLoading: isLoadingLocations } = useLocations();
  const { getPositions, isLoading: isLoadingPositions } = usePositions();
  const { getGroups, isLoading: isLoadingGroups } = useGroups();

  const classes = useStyles();

  const [positions, setPositions] = useState([]);
  const [locations, setLocations] = useState([]);
  const [groups, setGroups] = useState([]);

  const { type, items } = assignmentData;

  useEffect(() => {
    getPositions({ filters: { active: true }, sort: "name" }).then(response =>
      setPositions(response.data)
    );
    getLocations({ filters: { active: true }, sort: "name" }).then(response =>
      setLocations(response.data)
    );
    getGroups({
      fields: { group: ["name"] },
      filters: { active: true },
      sort: "name"
    }).then(response => setGroups(response.data));
  }, []);

  const itemCollection = {
    position: positions,
    location: locations,
    group: groups
  }[type];

  const isLoading = {
    position: isLoadingPositions,
    location: isLoadingLocations,
    group: isLoadingGroups
  }[type];

  const setSelectedItems = useCallback(
    items => {
      onAssignmentDataChange({
        type,
        items
      });
    },
    [onAssignmentDataChange, type]
  );

  const setType = useCallback(
    type => {
      onAssignmentDataChange({
        type,
        items: []
      });
    },
    [onAssignmentDataChange, type]
  );

  const allSelected = useCallback(
    () => itemCollection.length > 0 && arraysEqual(items, itemCollection),
    [items, itemCollection]
  );

  const onAllSelected = () => {
    if (allSelected()) {
      setSelectedItems([]);
    } else {
      setSelectedItems([...itemCollection]);
    }
  };

  const filterOptions = (options, { inputValue }) =>
    options.filter(
      ({ name }) => name.toLowerCase().indexOf(inputValue.toLowerCase()) > -1
    );

  const renderOption = (props, option) => {
    return (
      <li {...props}>
        <Typography
          noWrap
          sx={{ textOverflow: "ellipsis", overflow: "hidden" }}
          data-qa="assignment-option"
        >
          {option.name}
        </Typography>
      </li>
    );
  };

  return (
    <Grid
      container
      spacing={2}
      sx={{ mt: 1.5, mb: 1 }}
      data-qa="assignment-selector-container"
    >
      <Grid item md={4}>
        <Box display="flex" flexDirection="column">
          <Typography sx={{ mb: 1 }} variant="caption">
            {t("type.label")}
          </Typography>
          <TextField
            select
            fullWidth
            variant="filled"
            InputProps={{
              disableUnderline: true
            }}
            value={type}
            onChange={event => setType(event.target.value)}
            hiddenLabel
            SelectProps={{
              SelectDisplayProps: {
                "data-qa": "assignment-selector-type-field"
              }
            }}
          >
            {ASSIGNMENT_TYPES.map(type => (
              <MenuItem
                value={type}
                key={type}
                data-qa={`assignment-selector-type-${type}`}
              >
                {t("type.items." + type)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Grid>
      <Grid item md={8}>
        <Box display="flex" flexDirection="column">
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            mt="-11px"
          >
            <Typography sx={{ mr: 3 }} variant="caption">
              {t(type + ".label")}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={allSelected()}
                  onChange={onAllSelected}
                  className={classes.checkbox}
                  color="primary"
                />
              }
              label={
                <Typography variant="caption">{t("selectAll")}</Typography>
              }
            />
          </Box>
          <SearchInput
            items={itemCollection}
            renderOption={renderOption}
            getOptionLabel={({ name }) => name}
            filterOptions={filterOptions}
            placeholder={!items.length ? t(`type.items.${type}`) : ""}
            itemSize={40}
            multiple
            autoComplete
            limitTags={5}
            filterSelectedOptions
            value={items}
            onChange={(e, selectedItems) => setSelectedItems(selectedItems)}
            selectAllLabel={t("selectAll")}
            loading={isLoading}
          />
        </Box>
      </Grid>
    </Grid>
  );
};

export default AssignmentSelector;
